import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import { logDebug } from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Public newsletter capture card. Stored locally on success so the returning-visitor
// whisper can greet them by name on later visits.
export const NewsletterCapture = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [message, setMessage] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setStatus('error');
      setMessage('Please enter a valid email.');
      return;
    }
    setStatus('submitting');
    setMessage('');
    try {
      const res = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, full_name: name || null, source: 'landing_hero' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Unable to subscribe');
      // Remember this visitor for a personalized welcome on return.
      try {
        if (name) localStorage.setItem('sds_visitor_name', name.trim());
        localStorage.setItem('sds_visited', '1');
      } catch (e) {
        // storage may be disabled — silent in production
        logDebug('NewsletterCapture: localStorage write failed', e);
      }
      setStatus('success');
      setMessage(data.already
        ? "You're already on the list — beautiful soul."
        : "A whisper has been sent to your inbox.");
      setEmail('');
      setName('');
    } catch (err) {
      setStatus('error');
      setMessage(err.message);
    }
  };

  return (
    <div className="mt-16 max-w-xl mx-auto" data-testid="newsletter-capture">
      <div className="glass-card glass-card-glow rounded-3xl p-7 md:p-9 text-center">
        <p className="text-[10px] uppercase tracking-[0.4em] text-[var(--accent-violet)] mb-3">For your soul</p>
        <h3 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl text-[var(--text-primary)] mb-2 glow-text-soft">
          Receive a whisper from the <span className="italic text-[var(--text-accent)]">Lotus Gate</span>
        </h3>
        <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-6">
          A quiet transmission from time to time. No noise, only soul.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <input
            data-testid="newsletter-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name (optional)"
            maxLength={120}
            className="w-full px-5 py-3.5 rounded-full bg-white/5 border border-white/10 text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--accent-gold)]/40 text-center text-sm"
          />
          <input
            data-testid="newsletter-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.sacred"
            className="w-full px-5 py-3.5 rounded-full bg-white/5 border border-white/10 text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--accent-gold)]/40 text-center text-sm"
          />
          <button
            data-testid="newsletter-submit-btn"
            type="submit"
            disabled={status === 'submitting'}
            className="reveal-button w-full px-6 py-3.5 rounded-full text-[var(--text-accent)] text-xs uppercase tracking-[0.25em] font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {status === 'submitting'
              ? 'Sending the whisper…'
              : (<><Sparkles size={14} /> Open the Gate</>)}
          </button>
        </form>
        {status === 'success' && (
          <p data-testid="newsletter-success" className="mt-5 text-sm text-[var(--accent-gold)] italic">
            {message}
          </p>
        )}
        {status === 'error' && (
          <p data-testid="newsletter-error" className="mt-5 text-sm text-red-300">
            {message}
          </p>
        )}
        <p className="mt-4 text-[10px] text-[var(--text-muted)]">
          Unsubscribe with one tap. We never share your email.
        </p>
      </div>
    </div>
  );
};

export default NewsletterCapture;
